import EmailVerification from '@pages/EmailVerification/EmailVerification';
import { LoginPage } from '@pages/Login/Login';
import { SignUpPage } from '@pages/SignUp/SignUp';
import Menu from '@pages/WrapperPages/Menu/Menu';
import { AuthBase } from '@pages/WrapperPages/AuthBase/AuthBase';
import { createBrowserRouter } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import ResetPassword from '@pages/ResetPassword/ResetPassword';
import RecoveryEmail from '@pages/RecoveryEmail/RecoveryEmail';
import ProductDetail from '@pages/ProductDetail/ProductDetail';
import { VerifyProfile } from '@pages/VerifyProfile/VerifyProfile';
import PageNotFound from '@pages/PageNotFound/PageNotFound';
import SubscriptionIntro from '@pages/Investment/SubscriptionIntro/SubscriptionIntro';
import SubscriptionBase from '@pages/WrapperPages/Subscription/SubscriptionBase';
import Amount from '@pages/Investment/Amount/Amount';
import PersonalInformation from '@pages/Investment/PersonalInformation/PersonalInformation';
import InvestmentProfile from '@pages/Investment/InvestmentProfile/InvestmentProfile';
import Progress from '@pages/Investment/Progress/Progress';
import TaxDetails from '@pages/Investment/TaxDetails/TaxDetails';
import InvestmentQuestionnaire from '@pages/Investment/InvestmentQuestionnaire/InvestmentQuestionnaire';
import ContactDetails from '@pages/Investment/ContactDetails/ContactDetails';
import BankDetails from '@pages/Investment/BankDetails/BankDetails';
import IdentityVerification from '@pages/Investment/IdentityVerification/IdentityVerification';
import IdentityVerificationIntro from '@pages/Investment/IdentityVerification/IdentityVerificationIntro';
import ProfileContainer from '@pages/Profile/ProfileContainer/ProfileContainer';
import PortfolioBase from '@pages/WrapperPages/PortfolioBase/PortfolioBase';
import SubscriptionUnavailable from '@pages/Investment/SubscriptionUnavailable/SubscriptionUnavailable';
import ContractSigning from '@pages/Investment/ContractSigning/ContractSigning';
import SigningComplete from '@pages/Investment/ContractSigning/SigningComplete';
import Referral from '@pages/Referral/Referral';
import InvestmentProfileInfo from '@pages/Investment/InvestmentProfileInfo/InvestmentProfileInfo';
import LoginRestricted, {
  LoginRestrictedError,
} from '@pages/LoginRestricted/LoginRestricted';
import Company from '@pages/Investment/Company/Company';
import UltimateBeneficialOwner from '@pages/Investment/UltimateBeneficialOwners/UltimateBeneficialOwners';
import CompanyAuthorizedSignatories from '@pages/Investment/CompanyAuthorizedSignatories/CompanyAuthorizedSignatories';
import Agents from '@pages/Agents/Agents';
import NomineeCustomer from '@pages/NomineeCustomer/NomineeCustomer';
import Crm from '@pages/WrapperPages/Crm/Crm';
import NomineeInvestmentDetails from '@pages/NomineeInvestmentDetails/NomineeInvestmentDetails';
import EmailVerificationRequest from '@pages/EmailVerificationRequest/EmailVerificationRequest';
import PasswordResetRequest from '@pages/PasswordResetRequest/PasswordResetRequest';
import ExternalContractSigning from '@pages/ExternalContractSigning/ExternalContractSigning';

export const routes = [
  {
    path: '*',
    element: <PageNotFound />,
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        path: '/',
        element: <Menu />,
        children: [
          // TODO: Uncomment when we have more than one product https://swise-it.atlassian.net/browse/SWIT-158
          // {
          //   path: '/',
          //   element: <Home />,
          // },
          {
            path: '/',
            element: <ProductDetail />,
            index: true,
          },
          {
            path: '/product/:id',
            element: <ProductDetail />,
            index: true,
          },
          {
            path: '/product/:id/:tabId',
            element: <ProductDetail />,
          },
          {
            path: '/investment-get-started/:productId/subscription-intro',
            element: <SubscriptionIntro />,
          },
          {
            path: '/profile',
            element: <ProfileContainer />,
          },
          {
            path: '/portfolio',
            element: <PortfolioBase />,
          },
          {
            path: '/portfolio/:tabName',
            element: <PortfolioBase />,
          },
          {
            path: '/referralProgram',
            element: <Referral />,
          },
          {
            path: '/agents',
            element: <Agents />,
          },
          {
            path: '/crm',
            element: <Crm />,
          },
          {
            path: '/crm/customers',
            element: <Crm />,
          },
          {
            path: '/crm/investments',
            element: <Crm />,
          },
          {
            path: '/crm/customer/:tabId',
            element: <NomineeCustomer />,
          },
          {
            path: '/crm/customer/:tabId/:customerId',
            element: <NomineeCustomer />,
          },
          {
            path: '/crm/investments/:transactionId',
            element: <NomineeInvestmentDetails />,
          },
        ],
      },
      {
        path: '/email-verification',
        element: <AuthBase />,
        children: [
          {
            path: '/email-verification',
            element: <EmailVerification />,
          },
        ],
      },
      {
        path: '/email-verification-request',
        element: <AuthBase />,
        children: [
          {
            path: '/email-verification-request',
            element: <EmailVerificationRequest />,
          },
        ],
      },
      {
        path: '/verify',
        element: <VerifyProfile />,
      },
      {
        path: '/signature-complete',
        element: <SigningComplete />,
      },
      {
        path: '/investment-subscription/:productId',
        element: <SubscriptionBase />,
        children: [
          {
            path: '/investment-subscription/:productId/amount',
            element: <Amount />,
          },
          {
            path: '/investment-subscription/:productId/profile',
            element: <InvestmentProfile />,
          },
          {
            path: '/investment-subscription/:productId/progress',
            element: <Progress />,
          },
          {
            path: '/investment-subscription/:productId/company',
            element: <Company />,
          },
          {
            path: '/investment-subscription/:productId/ubos',
            element: <UltimateBeneficialOwner />,
          },
          {
            path: '/investment-subscription/:productId/authorized-signatories',
            element: <CompanyAuthorizedSignatories />,
          },
          {
            path: '/investment-subscription/:productId/personal-details',
            element: <PersonalInformation />,
          },
          {
            path: '/investment-subscription/:productId/contact-details',
            element: <ContactDetails />,
          },
          {
            path: '/investment-subscription/:productId/tax-details',
            element: <TaxDetails />,
          },
          {
            path: '/investment-subscription/:productId/questionnaire',
            element: <InvestmentQuestionnaire />,
          },
          {
            path: '/investment-subscription/:productId/bank-details',
            element: <BankDetails />,
          },
          {
            path: '/investment-subscription/:productId/identity-verification-intro',
            element: <IdentityVerificationIntro />,
          },
          {
            path: '/investment-subscription/:productId/identity-verification',
            element: <IdentityVerification />,
          },
          {
            path: '/investment-subscription/:productId/unavailable',
            element: <SubscriptionUnavailable />,
          },
          {
            path: '/investment-subscription/:productId/contract-signature',
            element: <ContractSigning />,
          },
          {
            path: '/investment-subscription/:productId/investment-profile-info',
            element: <InvestmentProfileInfo />,
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: <PublicRoute />,
    children: [
      {
        path: '/',
        element: <AuthBase />,
        children: [
          {
            path: '/login',
            element: <LoginPage />,
          },
          {
            path: '/signup',
            element: <SignUpPage />,
          },
          {
            path: '/reset-password',
            element: <ResetPassword />,
          },
          {
            path: '/recovery-email-sent',
            element: <RecoveryEmail />,
          },
          {
            path: '/email-verification-request',
            element: <EmailVerificationRequest />,
          },
          {
            path: '/password-reset-request',
            element: <PasswordResetRequest />,
          },
        ],
      },
      {
        path: '/contract-signing',
        element: <ExternalContractSigning />,
      },
      {
        path: '/signature-complete',
        element: <SigningComplete />,
      },
      {
        path: '/suspended',
        element: <LoginRestricted type={LoginRestrictedError.Suspended} />,
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
